import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { paypalAPI } from '../services/api';
import PayPalButton from '../components/PayPalButton';
import './ConsultationBooking.css';

function ConsultationSuccess() {
  return (
    <div className="consult-page">
      <div className="consult-success">
        <div className="consult-success__icon">✓</div>
        <h1 className="consult-success__title">Booking Confirmed!</h1>
        <p className="consult-success__desc">
          Thank you for booking a consultation. We'll reach out to you within 24 hours to schedule your 30-minute session.
        </p>
        <p className="consult-success__email">Check your PayPal email for a payment receipt.</p>
        <a href="/dashboard" className="consult-success__btn">Back to Dashboard</a>
      </div>
    </div>
  );
}

export default function ConsultationBooking() {
  const { user } = useAuth();
  const isSuccessPage = window.location.pathname === '/consultation/success';

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [validated, setValidated] = useState(false); // show PayPal button after form OK
  const [paid, setPaid] = useState(false);

  if (isSuccessPage || paid) return <ConsultationSuccess />;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 2) { setError('Please enter your full name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid email address.'); return; }
    setError('');
    setValidated(true);
  };

  const handlePayPalCreate = async () => {
    const res = await paypalAPI.createConsultationOrder(name.trim(), email.trim(), message.trim());
    return res.data.orderID;
  };

  const handlePayPalApprove = async ({ orderID }) => {
    try {
      await paypalAPI.captureConsultationOrder(orderID);
      setPaid(true);
    } catch (err) {
      setError('Payment capture failed. Please contact support if you were charged.');
      setValidated(false);
    }
  };

  return (
    <div className="consult-page">
      <div className="consult-card">
        <div className="consult-card__header">
          <div className="consult-card__icon">💬</div>
          <h1 className="consult-card__title">Book a 1:1 Consultation</h1>
          <p className="consult-card__subtitle">30 minutes · $50 · Pay via PayPal</p>
        </div>

        <div className="consult-card__perks">
          <div className="perk">
            <span className="perk__icon">🎯</span>
            <span className="perk__text">Tailored advice for your team's needs</span>
          </div>
          <div className="perk">
            <span className="perk__icon">⚡</span>
            <span className="perk__text">Setup help, onboarding, or feature deep-dive</span>
          </div>
          <div className="perk">
            <span className="perk__icon">📅</span>
            <span className="perk__text">We reach out within 24h to schedule your slot</span>
          </div>
        </div>

        <form className="consult-form" onSubmit={handleFormSubmit}>
          <div className="consult-form__field">
            <label className="consult-form__label">Full Name</label>
            <input
              type="text"
              className="consult-form__input"
              placeholder="Harsimrat Singh"
              value={name}
              onChange={e => { setName(e.target.value); setError(''); setValidated(false); }}
              disabled={validated}
              required
            />
          </div>

          <div className="consult-form__field">
            <label className="consult-form__label">Email Address</label>
            <input
              type="email"
              className="consult-form__input"
              placeholder="you@example.com"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); setValidated(false); }}
              disabled={validated}
              required
            />
          </div>

          <div className="consult-form__field">
            <label className="consult-form__label">
              What would you like to discuss? <span className="consult-form__optional">(optional)</span>
            </label>
            <textarea
              className="consult-form__textarea"
              placeholder="E.g. setting up for a 50-person all-hands, custom enterprise plan, AI transcription questions..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              disabled={validated}
              rows={4}
            />
          </div>

          {error && <p className="consult-form__error">{error}</p>}

          {!validated && (
            <button type="submit" className="consult-form__submit">
              Continue to Payment →
            </button>
          )}
        </form>

        {validated && (
          <div className="consult-paypal">
            <p className="consult-paypal__label">Click below to pay $50 via PayPal</p>
            <PayPalButton
              createOrder={handlePayPalCreate}
              onApprove={handlePayPalApprove}
              onError={() => setError('PayPal encountered an error. Please try again.')}
            />
            <button
              className="consult-paypal__back"
              onClick={() => { setValidated(false); setError(''); }}
            >
              ← Edit details
            </button>
          </div>
        )}

        <p className="consult-form__secure">
          🔒 Secured by PayPal · Buyer protection included
        </p>
      </div>
    </div>
  );
}
